// Render Animation mode: bbox + per-field settings → MediaRecorder capture.
// Prefers MP4 when the browser supports it; otherwise WebM. No new deps.
// Output is browser-download only (object URL) — never uploaded/saved server-side.

export const ANIM_FIELDS = ['waterlevel', 'waterspeed', 'windspeed'];
const DEFAULT_ACTIVE_FIELDS = new Set(['waterlevel']);

class AnimAbortError extends Error {
  constructor(message = 'Animation stopped') {
    super(message);
    this.name = 'AnimAbortError';
  }
}

function sleep(ms) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

function doubleRaf() {
  return new Promise(resolve => {
    requestAnimationFrame(() => requestAnimationFrame(resolve));
  });
}

export function pickRecorderMime() {
  if (typeof MediaRecorder === 'undefined') return {mimeType: '', ext: 'webm'};
  const candidates = [
    {mimeType: 'video/mp4;codecs=avc1.42E01E', ext: 'mp4'},
    {mimeType: 'video/mp4', ext: 'mp4'},
    {mimeType: 'video/webm;codecs=vp9', ext: 'webm'},
    {mimeType: 'video/webm;codecs=vp8', ext: 'webm'},
    {mimeType: 'video/webm', ext: 'webm'}
  ];
  for (const c of candidates) {
    if (MediaRecorder.isTypeSupported(c.mimeType)) return c;
  }
  return {mimeType: '', ext: 'webm'};
}

/** Client-side download only — never POSTs/uploads to the server. */
export function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 8000);
}

export function viewStateForBbox(bbox, clampLat) {
  const west = Number(bbox.west);
  const east = Number(bbox.east);
  const south = Number(bbox.south);
  const north = Number(bbox.north);
  let lonSpan = east - west;
  if (lonSpan <= 0) lonSpan += 360;
  const latSpan = Math.max(0.01, north - south);
  const centerLon = west + lonSpan / 2;
  const centerLat = clampLat((south + north) / 2);
  // GlobeView zoom ≈ log2(360 / lonSpan) for a full-width equator band.
  const zoomLon = Math.log2(360 / Math.max(lonSpan, 0.05));
  const zoomLat = Math.log2(170 / latSpan);
  const zoom = Math.max(1.5, Math.min(Math.min(zoomLon, zoomLat) - 0.35, 11));
  return {
    longitude: ((centerLon + 540) % 360) - 180,
    latitude: centerLat,
    zoom,
    pitch: 0,
    bearing: 0
  };
}

function sanitizeFilename(s) {
  return String(s).replace(/[^\w.-]+/g, '_').replace(/_+/g, '_');
}

function drawTopRightOverlay(ctx, width, cycle, frameTime) {
  const pad = 18;
  const line1 = `Cycle: ${cycle || '(unknown)'}`;
  const line2 = String(frameTime || '');
  ctx.font = '600 22px sans-serif';
  const w1 = ctx.measureText(line1).width;
  ctx.font = '20px sans-serif';
  const w2 = ctx.measureText(line2).width;
  const boxW = Math.ceil(Math.max(w1, w2) + 36);
  const boxH = 72;
  const x = width - boxW - pad;
  const y = pad;
  ctx.fillStyle = 'rgba(15,23,42,0.75)';
  ctx.fillRect(x, y, boxW, boxH);
  ctx.fillStyle = '#fff';
  ctx.textAlign = 'right';
  ctx.font = '600 22px sans-serif';
  ctx.fillText(line1, x + boxW - 16, y + 30);
  ctx.font = '20px sans-serif';
  ctx.fillText(line2, x + boxW - 16, y + 56);
  ctx.textAlign = 'left';
  return {x, y, boxW, boxH};
}

function wrapText(ctx, text, maxWidth) {
  const words = String(text).split(/\s+/);
  const lines = [];
  let line = '';
  for (const word of words) {
    const next = line ? `${line} ${word}` : word;
    if (ctx.measureText(next).width <= maxWidth) {
      line = next;
    } else {
      if (line) lines.push(line);
      line = word;
    }
  }
  if (line) lines.push(line);
  return lines.length ? lines : [String(text)];
}

function drawColorbar(ctx, canvasW, canvasH, legend, belowY) {
  if (!legend) return;
  const stops = legend.stops || [[0, '#000'], [1, '#fff']];
  const dmin = Number(legend.domainMin);
  const dmax = Number(legend.domainMax);
  const opacity = Number.isFinite(legend.opacity) ? legend.opacity : 1;

  const pad = 18;
  const barW = 28;
  const barH = Math.min(320, Math.round(canvasH * 0.45));
  const tickW = 56;
  const panelInnerW = barW + 12 + tickW;
  const titleMaxW = Math.max(panelInnerW, 140);
  const titleSize = 16;
  const tickSize = 15;
  const titleLineH = 20;

  ctx.save();
  ctx.font = `600 ${titleSize}px sans-serif`;
  const titleLines = wrapText(ctx, legend.label || '', titleMaxW);
  const titleH = titleLines.length * titleLineH + 10;
  const panelW = Math.max(panelInnerW + 28, titleMaxW + 28);
  const panelH = titleH + barH + 28;
  const px = canvasW - panelW - pad;
  const py = Math.max(pad, (belowY || pad) + 12);

  ctx.globalAlpha = 0.94;
  ctx.fillStyle = '#fff';
  ctx.strokeStyle = 'rgba(0,0,0,0.14)';
  ctx.lineWidth = 1;
  ctx.beginPath();
  if (typeof ctx.roundRect === 'function') {
    ctx.roundRect(px, py, panelW, panelH, 8);
  } else {
    ctx.rect(px, py, panelW, panelH);
  }
  ctx.fill();
  ctx.stroke();

  ctx.globalAlpha = 1;
  ctx.fillStyle = '#111';
  ctx.textAlign = 'center';
  ctx.font = `600 ${titleSize}px sans-serif`;
  titleLines.forEach((line, i) => {
    ctx.fillText(line, px + panelW / 2, py + 22 + i * titleLineH);
  });

  const barX = px + (panelW - panelInnerW) / 2;
  const barY = py + titleH + 6;
  const grad = ctx.createLinearGradient(0, barY + barH, 0, barY);
  for (const [t, c] of stops) {
    grad.addColorStop(Math.max(0, Math.min(1, t)), c);
  }
  ctx.globalAlpha = opacity;
  ctx.fillStyle = grad;
  ctx.fillRect(barX, barY, barW, barH);
  ctx.globalAlpha = 1;
  ctx.strokeStyle = '#888';
  ctx.lineWidth = 1.5;
  ctx.strokeRect(barX, barY, barW, barH);

  ctx.fillStyle = '#111';
  ctx.font = `${tickSize}px sans-serif`;
  ctx.textAlign = 'left';
  const nTicks = 7;
  for (let i = 0; i < nTicks; i++) {
    const frac = i / (nTicks - 1);
    const value = dmax - frac * (dmax - dmin);
    const ty = barY + frac * barH;
    ctx.fillText(Number.isFinite(value) ? value.toFixed(1) : '', barX + barW + 8, ty + 5);
  }
  ctx.restore();
}

/** Prefer manual frame capture so encode pacing controls duration. */
function openCaptureStream(canvas) {
  try {
    const stream = canvas.captureStream(0);
    const track = stream.getVideoTracks()[0];
    if (track && typeof track.requestFrame === 'function') {
      return {stream, track, manual: true};
    }
    stream.getTracks().forEach(t => t.stop());
  } catch {
    // fall through
  }
  const stream = canvas.captureStream(30);
  const track = stream.getVideoTracks()[0];
  if (track) track.enabled = false;
  return {stream, track, manual: false};
}

/**
 * Hold the current canvas image in the recorded timeline for frameHoldMs.
 * Call only during the encode pass (no tile loads).
 */
async function holdEncodedFrame(track, manual, frameHoldMs, deadlineMs) {
  const emitFps = 30;
  const interval = 1000 / emitFps;
  if (manual) {
    while (performance.now() < deadlineMs) {
      track.requestFrame();
      const remaining = deadlineMs - performance.now();
      if (remaining <= 0) break;
      await sleep(Math.min(interval, remaining));
    }
    track.requestFrame();
    return;
  }
  if (track) track.enabled = true;
  const remaining = deadlineMs - performance.now();
  if (remaining > 0) await sleep(remaining);
  if (track) track.enabled = false;
}

function canvasToImageBitmap(canvas) {
  return createImageBitmap(canvas);
}

function releaseFrameCache(frames) {
  if (!frames) return;
  for (let i = 0; i < frames.length; i++) {
    try {
      frames[i]?.close?.();
    } catch {
      // already closed
    }
    frames[i] = null;
  }
  frames.length = 0;
}

function releaseCaptureCanvas(canvas) {
  if (!canvas) return;
  try {
    const c = canvas.getContext('2d');
    if (c) c.clearRect(0, 0, canvas.width, canvas.height);
  } catch {
    // ignore
  }
  canvas.width = 0;
  canvas.height = 0;
}

function stopMediaStream(stream) {
  if (!stream) return;
  for (const tr of stream.getTracks()) {
    try {
      tr.stop();
    } catch {
      // ignore
    }
  }
}

/**
 * @param {object} host callbacks into app.js
 */
export function initRenderAnimation(host) {
  const btn = document.getElementById('renderAnimationBtn');
  const modal = document.getElementById('animModal');
  if (!btn || !modal) return;

  const ANIM_PASSWORD = 'armycorps';
  const authGate = document.getElementById('animAuthGate');
  const modalMain = document.getElementById('animModalMain');
  const authPassword = document.getElementById('animAuthPassword');
  const authSubmit = document.getElementById('animAuthSubmit');
  const authError = document.getElementById('animAuthError');

  const closeBtn = document.getElementById('animModalClose');
  const cancelBtn = document.getElementById('animCancelBtn');
  const startBtn = document.getElementById('animStartBtn');
  const stopBtn = document.getElementById('animStopBtn');
  const drawBtn = document.getElementById('animDrawBboxBtn');
  const fieldsEl = document.getElementById('animFields');
  const cycleHint = document.getElementById('animCycleHint');
  const statusEl = document.getElementById('animStatus');
  const startSel = document.getElementById('animStartTime');
  const endSel = document.getElementById('animEndTime');
  const durationIn = document.getElementById('animDurationSec');
  const fpsHint = document.getElementById('animFpsHint');
  const westIn = document.getElementById('animWest');
  const southIn = document.getElementById('animSouth');
  const eastIn = document.getElementById('animEast');
  const northIn = document.getElementById('animNorth');
  const overlay = document.getElementById('animBboxOverlay');
  const rectEl = document.getElementById('animBboxRect');
  const banner = document.getElementById('animDrawBanner');
  const bannerText = document.getElementById('animDrawBannerText');

  const fieldCards = new Map();
  let drawing = false;
  let dragOrigin = null;
  let recording = false;
  let abortRequested = false;
  let abortDraw = null;
  let authUnlocked = false;
  let authAttemptUsed = false;

  function throwIfAborted() {
    if (abortRequested) throw new AnimAbortError();
  }

  function setBannerMessage(msg) {
    if (bannerText) bannerText.textContent = msg || '';
    else if (banner) banner.textContent = msg || '';
  }

  function setStatus(msg) {
    if (statusEl) statusEl.textContent = msg || '';
    if (recording) {
      setBannerMessage(msg || 'Recording…');
      banner?.classList.add('visible');
    }
  }

  function showStopButton(show) {
    if (!stopBtn) return;
    stopBtn.classList.toggle('hidden', !show);
  }

  function buildFieldCards() {
    fieldsEl.innerHTML = '';
    fieldCards.clear();
    const colormaps = host.getColormaps();
    const displayFields = host.getDisplayFields();

    for (const key of ANIM_FIELDS) {
      const cfg = displayFields[key];
      const card = document.createElement('div');
      card.className = 'anim-field-card';
      card.dataset.field = key;

      const active = DEFAULT_ACTIVE_FIELDS.has(key);
      const check = document.createElement('button');
      check.type = 'button';
      check.className = active ? 'anim-field-check active' : 'anim-field-check';
      check.setAttribute('aria-pressed', active ? 'true' : 'false');
      check.title = 'Include this field';
      check.textContent = '✓';
      card.classList.toggle('inactive', !active);
      check.addEventListener('click', () => {
        const on = !check.classList.contains('active');
        check.classList.toggle('active', on);
        check.setAttribute('aria-pressed', on ? 'true' : 'false');
        card.classList.toggle('inactive', !on);
      });

      const title = document.createElement('div');
      title.className = 'anim-field-title';
      title.textContent = cfg.label;

      card.appendChild(check);
      card.appendChild(title);

      const basemap = makeSelect('Basemap', [
        ['osm', 'OpenStreetMap'],
        ['satellite', 'Satellite']
      ]);
      const cmapOpts = colormaps.map(c => [c.id, c.label]);
      const cmap = makeSelect('Colormap', cmapOpts, host.getDefaultColormapId());
      const opacity = makeRange('Transparency', 0, 100, host.getTransparencyPct());
      const [dmin, dmax] = cfg.domain;
      const minIn = makeNumber('Min Legend Value', dmin);
      const maxIn = makeNumber('Max Legend Value', dmax);
      const edges = makeCheckbox('Show Mesh Edges', false);
      const nhc = makeCheckbox('Show NHC Storm Tracks', false);

      card.appendChild(basemap.row);
      card.appendChild(cmap.row);
      card.appendChild(opacity.row);
      card.appendChild(minIn.row);
      card.appendChild(maxIn.row);
      card.appendChild(edges.row);
      card.appendChild(nhc.row);
      fieldsEl.appendChild(card);

      fieldCards.set(key, {
        card,
        check,
        basemap: basemap.el,
        colormap: cmap.el,
        opacity: opacity.el,
        opacityValue: opacity.valueEl,
        min: minIn.el,
        max: maxIn.el,
        edges: edges.el,
        nhc: nhc.el
      });
    }
  }

  function makeSelect(labelText, options, selected) {
    const row = document.createElement('div');
    row.className = 'option-row';
    const lab = document.createElement('label');
    lab.textContent = labelText;
    const el = document.createElement('select');
    for (const [value, text] of options) {
      const opt = document.createElement('option');
      opt.value = value;
      opt.textContent = text;
      if (value === selected) opt.selected = true;
      el.appendChild(opt);
    }
    row.appendChild(lab);
    row.appendChild(el);
    return {row, el};
  }

  function makeNumber(labelText, value) {
    const row = document.createElement('div');
    row.className = 'option-row';
    const lab = document.createElement('label');
    lab.textContent = labelText;
    const el = document.createElement('input');
    el.type = 'number';
    el.step = 'any';
    el.value = String(value);
    row.appendChild(lab);
    row.appendChild(el);
    return {row, el};
  }

  function makeRange(labelText, min, max, value) {
    const row = document.createElement('div');
    row.className = 'option-row';
    const lab = document.createElement('label');
    const valueEl = document.createElement('span');
    valueEl.style.cssText = 'font-weight:400;float:right;color:#555;';
    valueEl.textContent = `${value}%`;
    lab.appendChild(document.createTextNode(`${labelText} `));
    lab.appendChild(valueEl);
    const el = document.createElement('input');
    el.type = 'range';
    el.min = String(min);
    el.max = String(max);
    el.step = '1';
    el.value = String(value);
    el.addEventListener('input', () => {
      valueEl.textContent = `${el.value}%`;
    });
    row.appendChild(lab);
    row.appendChild(el);
    return {row, el, valueEl};
  }

  function makeCheckbox(labelText, checked) {
    const row = document.createElement('div');
    row.className = 'option-row checkbox-row';
    const lab = document.createElement('label');
    const el = document.createElement('input');
    el.type = 'checkbox';
    el.checked = checked;
    lab.appendChild(el);
    lab.appendChild(document.createTextNode(` ${labelText}`));
    row.appendChild(lab);
    return {row, el};
  }

  function fillTimeSelects() {
    const times = host.getTimesteps() || [];
    startSel.innerHTML = '';
    endSel.innerHTML = '';
    times.forEach((ts, i) => {
      const label = String(ts);
      const o1 = document.createElement('option');
      o1.value = String(i);
      o1.textContent = label;
      startSel.appendChild(o1);
      const o2 = document.createElement('option');
      o2.value = String(i);
      o2.textContent = label;
      endSel.appendChild(o2);
    });
    if (times.length) {
      startSel.value = '0';
      endSel.value = String(times.length - 1);
    }
    updateFpsHint();
  }

  function syncFromLiveUi() {
    const live = host.getLiveSettings();
    for (const key of ANIM_FIELDS) {
      const ui = fieldCards.get(key);
      if (!ui) continue;
      ui.basemap.value = live.basemapStyle;
      ui.colormap.value = live.colormapId;
      ui.opacity.value = String(live.transparencyPct);
      ui.opacityValue.textContent = `${live.transparencyPct}%`;
      ui.edges.checked = live.showEdges;
      ui.nhc.checked = live.showOfcl;
      const cfg = host.getDisplayFields()[key];
      if (key === live.displayField) {
        ui.min.value = String(live.domainMin);
        ui.max.value = String(live.domainMax);
      } else {
        ui.min.value = String(cfg.domain[0]);
        ui.max.value = String(cfg.domain[1]);
      }
    }
  }

  function resetAuthGate() {
    authUnlocked = false;
    authAttemptUsed = false;
    authGate?.classList.remove('hidden');
    modalMain?.classList.add('hidden');
    if (authPassword) {
      authPassword.value = '';
      authPassword.disabled = false;
    }
    if (authSubmit) authSubmit.disabled = false;
    if (authError) authError.textContent = '';
  }

  function showAnimControls() {
    authUnlocked = true;
    authGate?.classList.add('hidden');
    modalMain?.classList.remove('hidden');
    fillTimeSelects();
    syncFromLiveUi();
    const cycle = host.getForecastCycle() || '(unknown)';
    const mime = pickRecorderMime();
    cycleHint.textContent =
      `Cycle: ${cycle}. Output: ${mime.ext.toUpperCase()}` +
      (mime.mimeType ? ` (${mime.mimeType})` : ' — MediaRecorder unavailable') +
      '.';
    setStatus('');
    updateFpsHint();
  }

  function tryUnlock() {
    if (authUnlocked || authAttemptUsed) return;
    authAttemptUsed = true;
    const entered = (authPassword?.value || '').trim();
    if (entered === ANIM_PASSWORD) {
      if (authError) authError.textContent = '';
      showAnimControls();
      return;
    }
    if (authPassword) {
      authPassword.value = '';
      authPassword.disabled = true;
    }
    if (authSubmit) authSubmit.disabled = true;
    if (authError) {
      authError.textContent =
        'Wrong password. Close this dialog to return to the viewer.';
    }
  }

  function openModal() {
    if (!host.getManifest()) {
      // Brief status via alert is avoided; open gate and show message there.
      resetAuthGate();
      if (authError) authError.textContent = 'Load a forecast cycle first.';
      if (authPassword) authPassword.disabled = true;
      if (authSubmit) authSubmit.disabled = true;
      authAttemptUsed = true;
      modal.classList.remove('hidden');
      return;
    }
    resetAuthGate();
    modal.classList.remove('hidden');
    authPassword?.focus();
  }

  function closeModal() {
    if (recording) return;
    modal.classList.add('hidden');
    stopDrawMode();
    resetAuthGate();
  }

  function readBbox() {
    const west = parseFloat(westIn.value);
    const south = parseFloat(southIn.value);
    const east = parseFloat(eastIn.value);
    const north = parseFloat(northIn.value);
    if (![west, south, east, north].every(Number.isFinite)) return null;
    if (!(north > south)) return null;
    return {west, south, east, north};
  }

  function writeBbox(bbox) {
    westIn.value = bbox.west.toFixed(4);
    southIn.value = bbox.south.toFixed(4);
    eastIn.value = bbox.east.toFixed(4);
    northIn.value = bbox.north.toFixed(4);
  }

  function stopDrawMode() {
    drawing = false;
    dragOrigin = null;
    overlay?.classList.remove('active');
    if (rectEl) rectEl.style.display = 'none';
    if (!recording) {
      banner?.classList.remove('visible');
      setBannerMessage('Drag on the map to select a region. Esc cancels.');
    }
    if (abortDraw) {
      window.removeEventListener('keydown', abortDraw);
      abortDraw = null;
    }
  }

  function startDrawMode() {
    modal.classList.add('hidden');
    drawing = true;
    showStopButton(false);
    setBannerMessage('Drag on the map to select a region. Esc cancels.');
    overlay.classList.add('active');
    banner.classList.add('visible');
    abortDraw = e => {
      if (e.key === 'Escape') {
        stopDrawMode();
        modal.classList.remove('hidden');
        setStatus('Bounding-box draw cancelled.');
      }
    };
    window.addEventListener('keydown', abortDraw);
  }

  function screenToLonLat(x, y) {
    const deck = host.getDeck();
    const viewport = deck?.getViewports()?.[0];
    if (!viewport) return null;
    const coord = viewport.unproject([x, y]);
    if (!coord) return null;
    return {lon: coord[0], lat: coord[1]};
  }

  overlay?.addEventListener('mousedown', e => {
    if (!drawing) return;
    e.preventDefault();
    dragOrigin = {x: e.clientX, y: e.clientY};
    rectEl.style.display = 'block';
    rectEl.style.left = `${e.clientX}px`;
    rectEl.style.top = `${e.clientY}px`;
    rectEl.style.width = '0px';
    rectEl.style.height = '0px';
  });

  overlay?.addEventListener('mousemove', e => {
    if (!drawing || !dragOrigin) return;
    const x0 = Math.min(dragOrigin.x, e.clientX);
    const y0 = Math.min(dragOrigin.y, e.clientY);
    const w = Math.abs(e.clientX - dragOrigin.x);
    const h = Math.abs(e.clientY - dragOrigin.y);
    rectEl.style.left = `${x0}px`;
    rectEl.style.top = `${y0}px`;
    rectEl.style.width = `${w}px`;
    rectEl.style.height = `${h}px`;
  });

  overlay?.addEventListener('mouseup', e => {
    if (!drawing || !dragOrigin) return;
    const x0 = Math.min(dragOrigin.x, e.clientX);
    const y0 = Math.min(dragOrigin.y, e.clientY);
    const x1 = Math.max(dragOrigin.x, e.clientX);
    const y1 = Math.max(dragOrigin.y, e.clientY);
    dragOrigin = null;
    if (x1 - x0 < 8 || y1 - y0 < 8) {
      setStatus('Drag a larger box.');
      return;
    }
    const a = screenToLonLat(x0, y1); // SW-ish in screen (y down)
    const b = screenToLonLat(x1, y0);
    const c = screenToLonLat(x0, y0);
    const d = screenToLonLat(x1, y1);
    stopDrawMode();
    modal.classList.remove('hidden');
    if (![a, b, c, d].every(Boolean)) {
      setStatus('Could not unproject that region — try again.');
      return;
    }
    const lons = [a.lon, b.lon, c.lon, d.lon];
    const lats = [a.lat, b.lat, c.lat, d.lat];
    writeBbox({
      west: Math.min(...lons),
      east: Math.max(...lons),
      south: Math.min(...lats),
      north: Math.max(...lats)
    });
    setStatus('Bounding box set.');
  });

  function collectJobs() {
    const jobs = [];
    for (const key of ANIM_FIELDS) {
      const ui = fieldCards.get(key);
      if (!ui || !ui.check.classList.contains('active')) continue;
      jobs.push({
        field: key,
        basemapStyle: ui.basemap.value,
        colormapId: ui.colormap.value,
        dataOpacity: 1 - parseInt(ui.opacity.value, 10) / 100,
        domainMin: parseFloat(ui.min.value),
        domainMax: parseFloat(ui.max.value),
        showEdges: ui.edges.checked,
        showOfcl: ui.nhc.checked
      });
    }
    return jobs;
  }

  function selectedFrameCount() {
    const t0 = parseInt(startSel.value, 10);
    const t1 = parseInt(endSel.value, 10);
    if (!Number.isFinite(t0) || !Number.isFinite(t1) || t1 < t0) return 0;
    return t1 - t0 + 1;
  }

  function updateFpsHint() {
    if (!fpsHint) return;
    const n = selectedFrameCount();
    const sec = readDurationSec();
    if (!n) {
      fpsHint.textContent = 'Select a start/end time to see playback rate.';
      return;
    }
    const fps = n / sec;
    fpsHint.textContent =
      `${n} frames over ${sec}s → ${fps.toFixed(1)} fps ` +
      `(all timesteps included; only playback speed changes).`;
  }

  function readDurationSec() {
    let sec = parseFloat(durationIn?.value);
    if (!Number.isFinite(sec) || sec < 1) sec = 8;
    if (sec > 600) sec = 600;
    return sec;
  }

  async function recordOne(job, t0, t1, bbox, cycle, durationSec) {
    throwIfAborted();
    const mime = pickRecorderMime();
    const deck = host.getDeck();
    const deckCanvas = deck?.getCanvas?.();
    if (!deckCanvas) throw new Error('Map canvas not ready');
    if (typeof MediaRecorder === 'undefined') {
      throw new Error('MediaRecorder is not available in this browser');
    }

    host.setShowStations?.(false);
    await host.applyAnimSettings(job);
    throwIfAborted();
    await host.fitViewToBbox(bbox);
    throwIfAborted();
    await host.waitVisibleReady(45000);
    throwIfAborted();

    const numFrames = t1 - t0 + 1;
    // Playback rate: all timesteps kept; fps = numFrames / durationSec
    // e.g. 160 frames in 5s → 32 fps.
    const frameHoldMs = (durationSec * 1000) / numFrames;
    const fps = numFrames / durationSec;
    const label = host.getDisplayFields()[job.field].label;
    const times = host.getTimesteps();

    const capture = document.createElement('canvas');
    capture.width = deckCanvas.width;
    capture.height = deckCanvas.height;
    const ctx = capture.getContext('2d');

    const frames = [];
    let stream = null;
    let recorder = null;
    const chunks = [];

    try {
      // ---- Phase 1: render every timestep (may take minutes). Not recorded. ----
      for (let t = t0; t <= t1; t++) {
        throwIfAborted();
        setStatus(
          `Rendering ${label}: frame ${t - t0 + 1}/${numFrames} ` +
            `(encode at ${fps.toFixed(1)} fps → ${durationSec}s)`
        );
        host.setTimestep(t);
        await host.waitVisibleReady(45000);
        throwIfAborted();
        await doubleRaf();
        await sleep(40);
        throwIfAborted();

        ctx.drawImage(deckCanvas, 0, 0);
        const stamp = drawTopRightOverlay(ctx, capture.width, cycle, times[t] || `t=${t}`);
        const legend = host.getLegendDrawInfo?.();
        drawColorbar(ctx, capture.width, capture.height, legend, stamp.y + stamp.boxH);
        frames.push(await canvasToImageBitmap(capture));
      }

      // ---- Phase 2: encode only — wall clock ≈ durationSec → file length ≈ durationSec ----
      setStatus(
        `Encoding ${numFrames} frames @ ${fps.toFixed(1)} fps → ${durationSec}s video…`
      );
      const opened = openCaptureStream(capture);
      stream = opened.stream;
      const {track, manual} = opened;
      const options = mime.mimeType
        ? {mimeType: mime.mimeType, videoBitsPerSecond: 6_000_000}
        : {videoBitsPerSecond: 6_000_000};
      try {
        recorder = new MediaRecorder(stream, options);
      } catch {
        recorder = new MediaRecorder(stream);
      }

      recorder.ondataavailable = e => {
        if (e.data && e.data.size) chunks.push(e.data);
      };
      const stopped = new Promise((resolve, reject) => {
        recorder.onstop = resolve;
        recorder.onerror = () => reject(new Error('MediaRecorder error'));
      });

      recorder.start(100);
      // Prime encoder with first pixels before timed holds.
      ctx.drawImage(frames[0], 0, 0);
      if (manual) track.requestFrame();
      else if (track) track.enabled = true;
      await sleep(50);
      if (track && !manual) track.enabled = false;

      const encodeStarted = performance.now();
      for (let i = 0; i < frames.length; i++) {
        throwIfAborted();
        setStatus(
          `Encoding ${label}: ${i + 1}/${numFrames} @ ${fps.toFixed(1)} fps`
        );
        ctx.drawImage(frames[i], 0, 0);
        // Free each bitmap as soon as it has been drawn into the encoder.
        try {
          frames[i].close?.();
        } catch {
          // already closed
        }
        frames[i] = null;
        const deadline = encodeStarted + (i + 1) * frameHoldMs;
        await holdEncodedFrame(track, manual, frameHoldMs, deadline);
      }

      if (recorder.state !== 'inactive') recorder.stop();
      await stopped;
      stopMediaStream(stream);
      stream = null;
      recorder = null;

      if (abortRequested) throw new AnimAbortError();

      const blob = new Blob(chunks, {type: mime.mimeType || 'video/webm'});
      chunks.length = 0;
      const ext = (blob.type || mime.mimeType || '').includes('mp4') ? 'mp4' : mime.ext;
      const fname = sanitizeFilename(
        `stofs_${cycle || 'cycle'}_${job.field}_${times[t0]}_to_${times[t1]}.${ext}`
      );
      downloadBlob(blob, fname);
      return fname;
    } finally {
      // Always drop frame bitmaps, recorder chunks, stream tracks, and canvas backing store.
      releaseFrameCache(frames);
      chunks.length = 0;
      try {
        if (recorder && recorder.state !== 'inactive') recorder.stop();
      } catch {
        // ignore
      }
      stopMediaStream(stream);
      releaseCaptureCanvas(capture);
    }
  }

  async function startRender() {
    if (recording) return;
    const bbox = readBbox();
    if (!bbox) {
      setStatus('Set a valid bounding box (west/south/east/north).');
      return;
    }
    const t0 = parseInt(startSel.value, 10);
    const t1 = parseInt(endSel.value, 10);
    if (!Number.isFinite(t0) || !Number.isFinite(t1) || t1 < t0) {
      setStatus('Choose a valid start/end time within this cycle.');
      return;
    }
    const jobs = collectJobs();
    if (!jobs.length) {
      setStatus('Activate at least one display field (checkmark).');
      return;
    }
    const numFrames = t1 - t0 + 1;
    const durationSec = readDurationSec();

    recording = true;
    abortRequested = false;
    startBtn.disabled = true;
    btn.disabled = true;
    modal.classList.add('hidden');
    showStopButton(true);
    setBannerMessage('Recording…');
    banner.classList.add('visible');
    const snapshot = host.snapshotViewerState();
    const cycle = host.getForecastCycle();
    let stoppedByUser = false;
    let completed = 0;

    try {
      host.setShowStations?.(false);
      host.setUiChromeVisible(false);
      banner.classList.add('visible');
      showStopButton(true);
      for (let i = 0; i < jobs.length; i++) {
        throwIfAborted();
        setStatus(
          `Animation ${i + 1}/${jobs.length}: render frames, then encode ${durationSec}s…`
        );
        const name = await recordOne(jobs[i], t0, t1, bbox, cycle, durationSec);
        completed += 1;
        setStatus(`Downloaded ${name}`);
        await sleep(300);
      }
      setStatus(
        `Done — ${completed} animation${completed > 1 ? 's' : ''} downloaded.`
      );
    } catch (err) {
      if (err instanceof AnimAbortError) {
        stoppedByUser = true;
        setStatus(
          completed > 0
            ? `Stopped. ${completed} file${completed > 1 ? 's' : ''} already downloaded; remainder discarded.`
            : 'Stopped.'
        );
      } else {
        console.error('[anim]', err);
        setStatus(`Error: ${err.message || err}`);
      }
    } finally {
      abortRequested = false;
      host.setShowStations?.(true);
      await host.restoreViewerState(snapshot);
      host.setUiChromeVisible(true);
      showStopButton(false);
      banner.classList.remove('visible');
      setBannerMessage('Drag on the map to select a region. Esc cancels.');
      recording = false;
      startBtn.disabled = false;
      btn.disabled = false;
      modal.classList.remove('hidden');
      void stoppedByUser;
    }
  }

  function requestStop() {
    if (!recording) return;
    abortRequested = true;
    setStatus('Stopping…');
  }

  buildFieldCards();

  startSel?.addEventListener('change', updateFpsHint);
  endSel?.addEventListener('change', updateFpsHint);
  durationIn?.addEventListener('input', updateFpsHint);
  durationIn?.addEventListener('change', updateFpsHint);

  btn.addEventListener('click', openModal);
  closeBtn?.addEventListener('click', closeModal);
  cancelBtn?.addEventListener('click', closeModal);
  authSubmit?.addEventListener('click', tryUnlock);
  authPassword?.addEventListener('keydown', e => {
    if (e.key === 'Enter') {
      e.preventDefault();
      tryUnlock();
    }
  });
  drawBtn?.addEventListener('click', startDrawMode);
  stopBtn?.addEventListener('click', requestStop);
  startBtn?.addEventListener('click', () => {
    startRender().catch(err => {
      console.error(err);
      setStatus(`Error: ${err.message || err}`);
    });
  });
}
